import { useState, useCallback, useMemo } from 'react';

import PropTypes from 'prop-types';
import { BsPeopleFill, BsStopCircle } from 'react-icons/bs';

import { useCreditsWallet } from '#creditFeature/models/credits/queries';
import Button from '#globalComponents/Button/Button';
import FormField from '#globalComponents/Forms/FormField';
import TextInput from '#globalComponents/Forms/TextInput';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import RadioButton from '#globalComponents/RadioButton/RadioButton';
import RolePriorityPicker from '#globalComponents/RolePriorityPicker/RolePriorityPicker';
import ActionScopeButton from '#globalComponents/Table/actions/ActionScopeButton';
import { useCapabilityCheck } from '#globalHooks/useCapabilityCheck';
import useContactRoles from '#globalHooks/useContactRoles';
import {
	DEFAULT_MAX_CONTACTS,
	isValidMaxContacts,
	MAX_CONTACTS_MAX,
	MAX_CONTACTS_MIN,
} from '#leadOverviewFeature/constants/contactEnrichmentCriteria';
import { useLeadUI } from '#leadOverviewFeature/contexts/LeadUIContext';
import { useActionScope } from '#leadOverviewFeature/hooks/useActionScope';
import { PHONE_ADDON } from '#lib/addons/addonConfigs';
import { CONTACT_SELECTION_HINT } from '#lib/constants/entityLabels';

import EnrichmentCostHint from './EnrichmentCostHint';
import PhoneRevealOption from './PhoneRevealOption';

const DEFAULT_ROLE_PRIORITY = ['ceo'];

const initialCriteria = () => ({
	contactMode: 'singleCeoOnly',
	rolePriority: DEFAULT_ROLE_PRIORITY,
	maxContacts: String(DEFAULT_MAX_CONTACTS),
	revealPhones: false,
});

const SectionLabel = ({ children }) => (
	<p className="mb-2 text-xs font-medium uppercase tracking-wide text-bgBase-800">{children}</p>
);

SectionLabel.propTypes = { children: PropTypes.node.isRequired };

export const ContactEnrichmentModal = ({
	onConfirm,
	isLoading,
	onAbort,
	runningEnrichmentJob,
	isCancellingEnrichment,
}) => {
	const { hasCapability: hasPhoneAddon } = useCapabilityCheck(`${PHONE_ADDON.key}.access`);
	const { enrichableRoles } = useContactRoles();
	const { data: wallet } = useCreditsWallet();
	const [isOpen, setIsOpen] = useState(false);
	const [criteria, setCriteria] = useState(initialCriteria);
	const { contactMode, rolePriority, maxContacts, revealPhones } = criteria;

	const actionScope = useActionScope();
	const { leadActionTarget, hasContactSelection } = useLeadUI();

	const close = useCallback(() => setIsOpen(false), []);
	const update = useCallback((patch) => setCriteria((prev) => ({ ...prev, ...patch })), []);

	const isSingleCeo = contactMode === 'singleCeoOnly';
	const parsedMax = Number(maxContacts);
	const effectiveMaxContacts = isSingleCeo ? 1 : parsedMax;
	const maxContactsValid = isSingleCeo || isValidMaxContacts(parsedMax);

	// The per-company cap counts contacts the company already holds; the row's contactCount
	// includes the company mailbox, which is not a contact.
	const heldContacts = useMemo(
		() =>
			actionScope.knownLeads.reduce((sum, lead) => {
				const persons = (Number(lead.contactCount) || 0) - (lead.emailAddress ? 1 : 0);
				return sum + Math.min(Math.max(0, persons), effectiveMaxContacts);
			}, 0),
		[actionScope.knownLeads, effectiveMaxContacts],
	);

	const handleConfirm = () => {
		const options = { maxContacts: effectiveMaxContacts, preferredChannels: revealPhones ? ['phone'] : [] };

		if (isSingleCeo) {
			options.contactRoles = ['ceo'];
		} else if (contactMode === 'contactRoles') {
			options.contactRoles = rolePriority;
		}
		// 'all' mode: no roles → backend searches every contactable role

		onConfirm(options);
		setCriteria(initialCriteria());
		setIsOpen(false);
	};

	const isConfirmDisabled =
		isLoading ||
		actionScope.count === 0 ||
		!maxContactsValid ||
		(contactMode === 'contactRoles' && rolePriority.length === 0);

	const trigger =
		runningEnrichmentJob || isCancellingEnrichment ? (
			<Button
				onClick={() => runningEnrichmentJob && onAbort(runningEnrichmentJob.bullJobId, 'contactEnrichment')}
				disabled={isCancellingEnrichment}
				variant="solid"
				color="red"
				size="md"
				className="inline-flex items-center gap-2"
			>
				<BsStopCircle className={`w-4 h-4 ${isCancellingEnrichment ? 'animate-spin' : ''}`} />
				{isCancellingEnrichment ? 'Wird abgebrochen...' : 'Abbrechen'}
			</Button>
		) : (
			<ActionScopeButton
				icon={
					<div className="relative inline-flex items-center justify-center">
						<span className="absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75 animate-[ping_1s_ease-in-out_infinite]"></span>
						<BsPeopleFill className="h-4 w-4 relative z-10" />
					</div>
				}
				onClick={() => setIsOpen(true)}
				disabled={isLoading || hasContactSelection}
				title={hasContactSelection ? CONTACT_SELECTION_HINT : undefined}
			>
				{isLoading ? 'Anreicherung läuft...' : 'Ansprechpartner anreichern'}
			</ActionScopeButton>
		);

	return (
		<div data-tour="data-enrichment">
			{trigger}
			<Modal isOpen={isOpen} onClose={close} size="m">
				<Panel.Header icon={BsPeopleFill} onClose={close} subtitle={`Angereichert werden ${leadActionTarget}`}>
					Ansprechpartner anreichern
				</Panel.Header>

				<Panel.Body className="space-y-5">
					<section>
						<SectionLabel>Wen</SectionLabel>
						<div className="space-y-1">
							<RadioButton
								name="contactMode"
								value="singleCeoOnly"
								checked={isSingleCeo}
								onChange={(value) => update({ contactMode: value })}
								label="Ein Geschäftsführer pro Firma"
								size="sm"
							/>
							<RadioButton
								name="contactMode"
								value="contactRoles"
								checked={contactMode === 'contactRoles'}
								onChange={(value) => update({ contactMode: value })}
								label="Rollen nach Priorität"
								size="sm"
							/>
							{contactMode === 'contactRoles' && (
								<div className="pl-6 pt-2 pb-1">
									<RolePriorityPicker
										value={rolePriority}
										onChange={(value) => update({ rolePriority: value })}
										options={enrichableRoles}
									/>
								</div>
							)}
							<RadioButton
								name="contactMode"
								value="all"
								checked={contactMode === 'all'}
								onChange={(value) => update({ contactMode: value })}
								label="Alle Ansprechpartner"
								size="sm"
							/>
						</div>
						{!isSingleCeo && (
							<div className="mt-3 pl-6">
								<FormField
									label="Maximal pro Firma"
									htmlFor="enrichment-max-contacts"
									hint={`${MAX_CONTACTS_MIN} bis ${MAX_CONTACTS_MAX} Ansprechpartner, bereits vorhandene zählen mit`}
								>
									<TextInput
										id="enrichment-max-contacts"
										type="number"
										min={MAX_CONTACTS_MIN}
										max={MAX_CONTACTS_MAX}
										value={maxContacts}
										onChange={(e) => update({ maxContacts: e.target.value })}
										className="max-w-[8rem]"
									/>
								</FormField>
							</div>
						)}
					</section>

					<PhoneRevealOption
						checked={revealPhones}
						onChange={(value) => update({ revealPhones: value })}
						hasPhoneAddon={hasPhoneAddon}
					/>
				</Panel.Body>

				<Panel.Footer className="flex-col items-stretch gap-3">
					{maxContactsValid ? (
						<EnrichmentCostHint
							companyCount={actionScope.count}
							maxContacts={effectiveMaxContacts}
							heldContacts={heldContacts}
							availableCredits={wallet?.contactCredits ?? null}
						/>
					) : (
						<p className="text-xs text-caution-700">Bitte 1 bis 50 Ansprechpartner pro Firma angeben.</p>
					)}
					<div className="flex shrink-0 justify-end gap-2">
						<Button type="button" variant="outline" size="md" onClick={close}>
							Abbrechen
						</Button>
						<Button onClick={handleConfirm} disabled={isConfirmDisabled} variant="solid" size="md">
							Jetzt anreichern
						</Button>
					</div>
				</Panel.Footer>
			</Modal>
		</div>
	);
};

ContactEnrichmentModal.propTypes = {
	onConfirm: PropTypes.func.isRequired,
	isLoading: PropTypes.bool,
	onAbort: PropTypes.func,
	runningEnrichmentJob: PropTypes.object,
	isCancellingEnrichment: PropTypes.bool,
};
